import React, { useEffect, useState, useCallback } from 'react'
import axios from 'axios'
import { STATUS_META } from './invoiceStatus'
import { isMockMode, mockGet, mockApprove, mockPay, mockVoid, mockAdjust, mockCreateAspireReceipt } from './mockData'

function fmtCurrency(n) {
  if (n == null) return '—'
  return '$' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function fmtDate(d) {
  if (!d) return '—'
  return new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })
}

function fmtDateShort(d) {
  if (!d) return '—'
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

function InfoRow({ label, value }) {
  return (
    <div style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
      <span style={{ fontSize: 11.5, color: 'var(--ink-3, #607d8b)', minWidth: 130, flexShrink: 0 }}>{label}</span>
      <span style={{ fontSize: 12.5, color: 'var(--ink, #263238)', fontWeight: 500 }}>{value || '—'}</span>
    </div>
  )
}

function SectionHead({ title }) {
  return (
    <div style={{
      fontWeight: 600, fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase',
      color: 'var(--ink-3, #607d8b)', padding: '12px 0 6px', borderBottom: '1px solid var(--line-1, #e0e8eb)',
      marginBottom: 10, marginTop: 16,
    }}>
      {title}
    </div>
  )
}

export default function InvoiceDetailDrawer({ url, invoiceId, onClose, onStateChange }) {
  const [invoice, setInvoice] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  // Adjust form state
  const [adjDesc, setAdjDesc] = useState('')
  const [adjAmount, setAdjAmount] = useState('')
  const [adjSubmitting, setAdjSubmitting] = useState(false)
  const [adjError, setAdjError] = useState(null)

  // Action states
  const [actionBusy, setActionBusy] = useState(false)
  const [actionError, setActionError] = useState(null)

  // Aspire receipt creation
  const [receiptBusy, setReceiptBusy] = useState(false)
  const [receiptError, setReceiptError] = useState(null)

  const [pricingAcreage, setPricingAcreage] = useState('')
  const [pricingRate, setPricingRate] = useState('')
  const [pricingReason, setPricingReason] = useState('')
  const [pricingBusy, setPricingBusy] = useState(false)
  const [pricingError, setPricingError] = useState(null)

  // On-demand invoice PDF generation
  const [invoicePdfBusy, setInvoicePdfBusy] = useState(false)
  const [invoicePdfError, setInvoicePdfError] = useState(null)

  // On-demand spray log PDF generation — one per owner mode
  const [subSprayPdfBusy, setSubSprayPdfBusy] = useState(false)
  const [subSprayPdfError, setSubSprayPdfError] = useState(null)
  const [hmiSprayPdfBusy, setHmiSprayPdfBusy] = useState(false)
  const [hmiSprayPdfError, setHmiSprayPdfError] = useState(null)

  const openBase64Pdf = (base64) => {
    const byteChars = atob(base64)
    const bytes = new Uint8Array(byteChars.length)
    for (let i = 0; i < byteChars.length; i++) bytes[i] = byteChars.charCodeAt(i)
    const blob = new Blob([bytes], { type: 'application/pdf' })
    const blobUrl = URL.createObjectURL(blob)
    window.open(blobUrl, '_blank')
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60000)
  }

  // Build a spray-log payload for either owner mode. Applicator info is always
  // the subcontractor (per business rules); only owner branding + customer of
  // record differ. The server applies HMI branding when documentOwner='hmi'.
  const buildSprayLogPayload = (inv, documentOwner) => {
    const op = inv.sprayOperation || {}
    const branding = inv.vendor?.portalBranding || {}
    return {
      documentOwner,
      vendorBranding: branding,
      // Customer of record — only used when documentOwner='hmi'
      endCustomerName: inv.entity?.name || '',
      endCustomerAddress: inv.entity?.physicalAddress || '',
      // billToName/billToAddress — used when documentOwner='subcontractor' (we are the sub's customer)
      billToName: 'HMI Commercial Landscaping',
      billToAddress: '',
      // Site
      cleanDate: new Date(op.date || op.created_at || inv.created_at || Date.now()).toLocaleDateString('en-US'),
      physicalLocation: inv.scheduleItem?.siteName || '',
      physicalAddress: (inv.scheduleItem?.address || inv.scheduleItem?.siteAddress) || '',
      // Applicator / licensing — always the sub's data
      bizName: inv.vendor?.name || branding.displayName || '',
      headerName: branding.displayName || inv.vendor?.name || '',
      pmdNum: inv.vendor?.pmdNumber || '',
      qualifyingParty: inv.vendor?.qualifyingParty || '',
      qpNum: inv.vendor?.applicatorLicense || '',
      techName: op.techName || inv.vendor?.qualifyingParty || '',
      licenseNum: op.licenseNum || inv.vendor?.applicatorLicense || '',
      licenseExp: op.licenseExp || '',
      applicatorPhone: inv.vendor?.applicatorPhone || inv.vendor?.phone || '',
      telephone: inv.vendor?.applicatorPhone || inv.vendor?.phone || '',
      // Timing + weather + water
      startTime: op.startTime || '',
      stopTime: op.stopTime || '',
      windDir: op.windDir || '',
      windSpeed: op.windSpeed != null ? op.windSpeed : '',
      temp: op.temp != null ? op.temp : '',
      waterStart: op.waterStart != null ? op.waterStart : 0,
      waterEnd: op.waterEnd != null ? op.waterEnd : 0,
      waterUsed: op.waterUsed != null ? op.waterUsed : (Number(op.waterStart || 0) - Number(op.waterEnd || 0)),
      // Chemicals
      incomingSolutions: Array.isArray(op.incomingSolutions) ? op.incomingSolutions : [],
      // Comments + optional signature
      comments: op.comments || op.notes || '',
      signature: op.signature || null
    }
  }

  const generateSprayLogPdfForOwner = async (owner) => {
    if (!invoice) return
    const isSub = owner === 'subcontractor'
    const setBusy = isSub ? setSubSprayPdfBusy : setHmiSprayPdfBusy
    const setErr = isSub ? setSubSprayPdfError : setHmiSprayPdfError
    const busy = isSub ? subSprayPdfBusy : hmiSprayPdfBusy
    if (busy) return
    setBusy(true)
    setErr(null)
    try {
      const base = url || ''
      const res = await axios.post(`${base}/api/spray-pdf-preview`, buildSprayLogPayload(invoice, owner))
      const base64 = res.data?.base64
      if (!base64) throw new Error('No PDF returned')
      openBase64Pdf(base64)
    } catch (err) {
      console.error(`[InvoiceDetailDrawer] generate ${owner} spray log error`, err)
      setErr(err.response?.data?.message || err.message || `Failed to generate ${owner} spray log PDF`)
    } finally {
      setBusy(false)
    }
  }

  const generateSubSprayLogPdf = () => generateSprayLogPdfForOwner('subcontractor')
  const generateHmiSprayLogPdf = () => generateSprayLogPdfForOwner('hmi')

  const buildInvoicePayload = (inv) => ({
    vendorBranding: inv.vendor?.portalBranding || {},
    vendorName: inv.vendor?.name || '',
    applicatorLicense: inv.vendor?.applicatorLicense || '',
    pmdNumber: inv.vendor?.pmdNumber || '',
    qualifyingParty: inv.vendor?.qualifyingParty || '',
    invoiceNumber: inv.invoiceNumber || '',
    invoiceDate: inv.created_at || new Date().toISOString(),
    dueDate: new Date(new Date(inv.created_at || Date.now()).getTime() + 30 * 24 * 60 * 60 * 1000).toISOString(),
    paymentTerms: 'Net 30',
    billToName: inv.entity?.name || 'HMI Commercial Landscaping',
    billToAddressLine1: inv.entity?.physicalAddress || '',
    billToAddressLine2: '',
    lineItems: [{
      description: `Herbicide application${inv.scheduleItem?.siteName ? ` — ${inv.scheduleItem.siteName}` : ''}`,
      ratePerAcre: inv.ratePerAcre || 0,
      acreage: inv.acreage || 0
    }],
    adjustments: (inv.adjustments || []).map(a => ({ description: a.description, amount: a.amount })),
    siteName: inv.scheduleItem?.siteName || '',
    siteAddress: (inv.scheduleItem?.address || inv.scheduleItem?.siteAddress) || '',
    sprayDate: inv.sprayOperation?.date || inv.created_at,
    notes: ''
  })

  const generateInvoicePdf = async () => {
    if (!invoice || invoicePdfBusy) return

    if (invoice.invoicePdfUrl) {
      window.open(invoice.invoicePdfUrl, '_blank')
      return
    }

    setInvoicePdfBusy(true)
    setInvoicePdfError(null)
    try {
      const base = url || ''
      const res = await axios.post(`${base}/api/invoice-pdf-preview`, buildInvoicePayload(invoice))
      const base64 = res.data?.base64
      if (!base64) throw new Error('No PDF returned')
      openBase64Pdf(base64)
    } catch (err) {
      console.error('[InvoiceDetailDrawer] generate invoice pdf error', err)
      setInvoicePdfError(err.response?.data?.message || err.message || 'Failed to generate invoice PDF')
    } finally {
      setInvoicePdfBusy(false)
    }
  }

  const fetchInvoice = useCallback(() => {
    if (!invoiceId) return
    if (!isMockMode() && !url) return
    setLoading(true)
    setError(null)
    const promise = isMockMode()
      ? mockGet(invoiceId).then(data => ({ data }))
      : axios.get(`${url}/api/subcontractor-invoices/${invoiceId}`)
    promise
      .then(res => {
        const nextInvoice = res.data.invoice || res.data
        setInvoice(nextInvoice)
        setPricingAcreage(nextInvoice?.acreage > 0 ? String(nextInvoice.acreage) : '')
        setPricingRate(nextInvoice?.ratePerAcre > 0 ? String(nextInvoice.ratePerAcre) : '')
      })
      .catch(err => {
        console.error('[InvoiceDetailDrawer] fetch error', err)
        setError('Failed to load invoice.')
      })
      .finally(() => setLoading(false))
  }, [url, invoiceId])

  useEffect(() => {
    setInvoice(null)
    setError(null)
    setAdjDesc('')
    setAdjAmount('')
    setAdjError(null)
    setActionError(null)
    setPricingReason('')
    setPricingError(null)
    fetchInvoice()
  }, [fetchInvoice])

  const doAction = useCallback((endpoint) => {
    if (!invoiceId) return
    if (!isMockMode() && !url) return
    setActionBusy(true)
    setActionError(null)
    let promise
    if (isMockMode()) {
      if (endpoint === 'approve') promise = mockApprove(invoiceId)
      else if (endpoint === 'pay') promise = mockPay(invoiceId)
      else if (endpoint === 'void') promise = mockVoid(invoiceId)
      else promise = Promise.reject(new Error('Unknown endpoint: ' + endpoint))
    } else {
      promise = axios.put(`${url}/api/subcontractor-invoices/${invoiceId}/${endpoint}`)
    }
    promise
      .then(() => {
        fetchInvoice()
        if (onStateChange) onStateChange()
      })
      .catch(err => {
        const msg = err.response?.data?.message || err.response?.data?.error || 'Action failed.'
        setActionError(msg)
      })
      .finally(() => setActionBusy(false))
  }, [url, invoiceId, fetchInvoice, onStateChange])

  const handleCreateAspireReceipt = useCallback(() => {
    if (!invoiceId || receiptBusy) return
    if (!isMockMode() && !url) return
    if (!window.confirm('Create a Purchase Receipt in Aspire for this invoice? This pushes real data into Aspire.')) return
    setReceiptBusy(true)
    setReceiptError(null)
    const promise = isMockMode()
      ? mockCreateAspireReceipt(invoiceId)
      : axios.post(`${url}/api/subcontractor-invoices/${invoiceId}/create-aspire-receipt`)
    promise
      .then(() => {
        fetchInvoice()
        if (onStateChange) onStateChange()
      })
      .catch(err => {
        const msg = err.response?.data?.message || err.response?.data?.error || err.message || 'Aspire receipt creation failed.'
        setReceiptError(msg)
        fetchInvoice() // server persists the error on the invoice; refresh to show lastAttempt state
      })
      .finally(() => setReceiptBusy(false))
  }, [url, invoiceId, receiptBusy, fetchInvoice, onStateChange])

  const handleApprove = () => doAction('approve')
  const handlePay = () => doAction('pay')
  const handleVoid = () => {
    if (!window.confirm('Void this invoice? This cannot be undone.')) return
    doAction('void')
  }

  const handleResolvePricing = useCallback(async (e) => {
    e.preventDefault()
    const acreage = Number(pricingAcreage)
    const ratePerAcre = Number(pricingRate)
    if (!(acreage > 0) || !(ratePerAcre > 0) || !pricingReason.trim()) {
      setPricingError('Acreage, rate, and review reason are required.')
      return
    }
    setPricingBusy(true)
    setPricingError(null)
    try {
      await axios.put(`${url}/api/subcontractor-invoices/${invoiceId}/pricing`, {
        acreage,
        ratePerAcre,
        reason: pricingReason.trim()
      })
      setPricingReason('')
      fetchInvoice()
      if (onStateChange) onStateChange()
    } catch (err) {
      setPricingError(err.response?.data?.message || err.response?.data?.error || 'Pricing review failed.')
    } finally {
      setPricingBusy(false)
    }
  }, [url, invoiceId, pricingAcreage, pricingRate, pricingReason, fetchInvoice, onStateChange])

  const handleAdjust = useCallback((e) => {
    e.preventDefault()
    if (!adjDesc.trim() || !adjAmount) {
      setAdjError('Description and amount are required.')
      return
    }
    const parsed = parseFloat(adjAmount)
    if (isNaN(parsed)) {
      setAdjError('Amount must be a number.')
      return
    }
    setAdjSubmitting(true)
    setAdjError(null)
    const payload = { description: adjDesc.trim(), amount: parsed }
    const promise = isMockMode()
      ? mockAdjust(invoiceId, payload)
      : axios.put(`${url}/api/subcontractor-invoices/${invoiceId}/adjust`, payload)
    promise
      .then(() => {
        setAdjDesc('')
        setAdjAmount('')
        fetchInvoice()
        if (onStateChange) onStateChange()
      })
      .catch(err => {
        const msg = err.response?.data?.message || err.response?.data?.error || 'Adjustment failed.'
        setAdjError(msg)
      })
      .finally(() => setAdjSubmitting(false))
  }, [url, invoiceId, adjDesc, adjAmount, fetchInvoice, onStateChange])

  if (!invoiceId) return null

  const statusMeta = invoice ? (STATUS_META[invoice.status] || { label: invoice.status, bg: '#f2f5f6', color: '#607d8b' }) : null
  const needsPricingReview = invoice?.pricingStatus === 'needs_review'
  const canResolvePricing = needsPricingReview && invoice?.pricingIssue !== 'missing_vendor'
  const pricingIssueMessage = ({
    missing_vendor: 'No subcontractor vendor is linked. Repair the vendor assignment before resolving pricing.',
    missing_scope_acreage: 'The completed scope resolved to zero acreage.'
  })[invoice?.pricingIssue] || 'The calculated amount was implausibly low.'
  const isReadOnly = invoice && (invoice.status === 'paid' || invoice.status === 'voided')
  const canApprove = invoice && !needsPricingReview && invoice.status === 'draft' && invoice.aspireReceipt?.receiptId
  const canPay = invoice && !needsPricingReview && invoice.status === 'approved'
  const canVoid = invoice && invoice.status !== 'paid' && invoice.status !== 'voided'
  const canAdjust = invoice && !needsPricingReview && invoice.status !== 'voided' && invoice.status !== 'paid'

  const drawerStyle = {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    background: 'var(--bg-panel, #fff)',
    borderLeft: '1px solid var(--line-1, #e0e8eb)',
    overflow: 'hidden',
  }

  return (
    <div style={drawerStyle}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12,
        padding: '14px 20px', borderBottom: '1px solid var(--line-1, #e0e8eb)',
        background: 'var(--bg-panel, #fff)',
      }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--ink, #263238)' }}>
            {invoice ? invoice.invoiceNumber : 'Invoice'}
          </div>
          {invoice && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
              <span style={{
                display: 'inline-block', padding: '2px 8px', borderRadius: 4,
                fontSize: 11, fontWeight: 600, background: statusMeta.bg, color: statusMeta.color,
              }}>
                {needsPricingReview ? 'Pricing Review' : statusMeta.label}
              </span>
              <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>
                {invoice.vendor?.name}
              </span>
            </div>
          )}
        </div>
        <button onClick={onClose} className="hmi-btn hmi-btn-ghost hmi-btn-sm" style={{ fontSize: 16, lineHeight: 1 }}>
          ✕
        </button>
      </div>

      {loading && (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--ink-3)', fontSize: 13 }}>
          Loading…
        </div>
      )}
      {error && (
        <div style={{ margin: 20, padding: '10px 14px', background: '#fff3d9', borderRadius: 6, color: '#8c5b00', fontSize: 13 }}>
          {error}
        </div>
      )}

      {invoice && !loading && (
        <div style={{ flex: 1, overflowY: 'auto', padding: '0 20px 24px' }}>

          {/* Action Buttons */}
          {!isReadOnly && (
            <div style={{ paddingTop: 16, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {canApprove && (
                <button
                  className="hmi-btn hmi-btn-primary hmi-btn-sm"
                  onClick={handleApprove}
                  disabled={actionBusy}
                >
                  Approve
                </button>
              )}
              {canPay && (
                <button
                  className="hmi-btn hmi-btn-primary hmi-btn-sm"
                  onClick={handlePay}
                  disabled={actionBusy}
                  style={{ background: '#1a5e35', borderColor: '#145229' }}
                >
                  Mark Paid
                </button>
              )}
              {canVoid && (
                <button
                  className="hmi-btn hmi-btn-outline hmi-btn-sm"
                  onClick={handleVoid}
                  disabled={actionBusy}
                  style={{ color: '#c62828', borderColor: '#ef9a9a' }}
                >
                  Void
                </button>
              )}
              {actionBusy && (
                <span style={{ fontSize: 12, color: 'var(--ink-3)', alignSelf: 'center' }}>Working…</span>
              )}
            </div>
          )}
          {isReadOnly && (
            <div style={{ paddingTop: 14, fontSize: 12, color: 'var(--ink-3)' }}>
              This invoice is {invoice.status} — no further actions available.
            </div>
          )}
          {actionError && (
            <div style={{ marginTop: 8, padding: '6px 12px', background: '#fff3d9', borderRadius: 5, fontSize: 12.5, color: '#8c5b00' }}>
              {actionError}
            </div>
          )}

          {/* Financial Summary */}
          <SectionHead title="Financial" />
          {needsPricingReview && (
            <form onSubmit={handleResolvePricing} style={{ padding: 12, marginBottom: 12, background: '#fff3d9', border: '1px solid #f0c66e', borderRadius: 6 }}>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: '#8c5b00', marginBottom: 4 }}>Pricing requires review</div>
              <div style={{ fontSize: 12, color: '#6f520e', marginBottom: 10 }}>
                {pricingIssueMessage}
              </div>
              {canResolvePricing && <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <input
                  type="number"
                  min="0.0001"
                  step="0.0001"
                  placeholder="Acreage"
                  value={pricingAcreage}
                  onChange={e => setPricingAcreage(e.target.value)}
                  style={{ width: '32%', height: 30, padding: '0 8px', border: '1px solid #d7b45e', borderRadius: 5 }}
                />
                <input
                  type="number"
                  min="0.01"
                  step="0.01"
                  placeholder="Rate / acre"
                  value={pricingRate}
                  onChange={e => setPricingRate(e.target.value)}
                  style={{ width: '32%', height: 30, padding: '0 8px', border: '1px solid #d7b45e', borderRadius: 5 }}
                />
                <input
                  type="text"
                  placeholder="Review reason"
                  value={pricingReason}
                  onChange={e => setPricingReason(e.target.value)}
                  style={{ flex: 1, height: 30, padding: '0 8px', border: '1px solid #d7b45e', borderRadius: 5 }}
                />
              </div>}
              {canResolvePricing && <button type="submit" className="hmi-btn hmi-btn-primary hmi-btn-sm" disabled={pricingBusy}>
                {pricingBusy ? 'Resolving…' : 'Confirm Pricing'}
              </button>}
              {pricingError && <div style={{ marginTop: 6, fontSize: 12, color: '#c62828' }}>{pricingError}</div>}
            </form>
          )}
          <InfoRow label="Invoice #" value={invoice.invoiceNumber} />
          <InfoRow label="Rate / Acre" value={invoice.ratePerAcre != null ? `$${invoice.ratePerAcre}/ac` : '—'} />
          <InfoRow label="Acreage" value={invoice.acreage != null ? `${invoice.acreage} ac` : '—'} />
          <InfoRow label="Base Amount" value={fmtCurrency(invoice.amount)} />
          {invoice.adjustments?.length > 0 && (
            <InfoRow
              label="Adjustments total"
              value={fmtCurrency(invoice.adjustments.reduce((sum, a) => sum + (a.amount || 0), 0))}
            />
          )}
          <InfoRow label="Final Amount" value={fmtCurrency(invoice.finalAmount)} />
          <InfoRow label="Created" value={fmtDate(invoice.created_at)} />
          {invoice.approvedAt && <InfoRow label="Approved" value={fmtDate(invoice.approvedAt)} />}
          {invoice.paidAt && <InfoRow label="Paid" value={fmtDate(invoice.paidAt)} />}
          {invoice.notes && <InfoRow label="Notes" value={invoice.notes} />}

          {/* Vendor Licensing */}
          <SectionHead title="Vendor Licensing" />
          <InfoRow label="Vendor" value={invoice.vendor?.name} />
          <InfoRow label="Applicator License" value={invoice.vendor?.applicatorLicense} />
          <InfoRow label="PMD Number" value={invoice.vendor?.pmdNumber} />
          <InfoRow label="Qualifying Party" value={invoice.vendor?.qualifyingParty} />

          {/* Site & Entity */}
          <SectionHead title="Site & Entity" />
          <InfoRow label="Entity" value={invoice.entity?.name} />
          {invoice.entity?.physicalAddress && (
            <InfoRow label="Address" value={invoice.entity.physicalAddress} />
          )}
          <InfoRow label="Site Name" value={invoice.scheduleItem?.siteName} />
          {(invoice.scheduleItem?.address || invoice.scheduleItem?.siteAddress) && (
            <InfoRow label="Site Address" value={(invoice.scheduleItem.address || invoice.scheduleItem.siteAddress)} />
          )}

          {/* Aspire Purchase Receipt */}
          <SectionHead title="Aspire" />
          {invoice.aspireReceipt?.receiptId ? (
            <>
              <InfoRow label="Purchase Receipt" value={`#${invoice.aspireReceipt.receiptId}`} />
              <InfoRow label="Pushed to Aspire" value={fmtDate(invoice.aspireReceipt.createdAt)} />
              <InfoRow label="Receipt Approved" value={invoice.aspireReceipt.approvedAt ? fmtDate(invoice.aspireReceipt.approvedAt) : 'No'} />
              {invoice.aspireReceipt.approvalError && (
                <div style={{ padding: '6px 12px', background: '#fdecea', borderRadius: 5, fontSize: 12.5, color: '#c62828', marginTop: 6 }}>
                  Receipt approval failed: {invoice.aspireReceipt.approvalError}
                </div>
              )}
            </>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 4 }}>
              {!needsPricingReview && (invoice.status === 'draft' || invoice.status === 'approved' || invoice.status === 'paid') ? (
                invoice.vendor?.aspireVendorId ? (
                  <button
                    className="hmi-btn hmi-btn-outline hmi-btn-sm"
                    onClick={handleCreateAspireReceipt}
                    disabled={receiptBusy}
                    style={{ alignSelf: 'flex-start' }}
                  >
                    {receiptBusy ? 'Creating Aspire Receipt…' : 'Create Aspire Receipt'}
                  </button>
                ) : (
                  <div style={{ fontSize: 12.5, color: 'var(--ink-3)' }}>
                    Vendor isn't linked to an Aspire vendor yet — set the Aspire Vendor on the vendor admin page to enable receipts.
                  </div>
                )
              ) : (
                <div style={{ fontSize: 12.5, color: 'var(--ink-3)' }}>
                  This invoice can't push a Purchase Receipt in its current status.
                </div>
              )}
              {(receiptError || invoice.aspireReceipt?.error) && (
                <div style={{ padding: '6px 12px', background: '#fdecea', borderRadius: 5, fontSize: 12.5, color: '#c62828' }}>
                  {receiptError || `Last attempt failed: ${invoice.aspireReceipt.error}`}
                </div>
              )}
            </div>
          )}

          {/* Spray Operation */}
          {invoice.sprayOperation && (
            <>
              <SectionHead title="Spray Operation" />
              <InfoRow label="Date" value={fmtDateShort(invoice.sprayOperation.date || invoice.sprayOperation.created_at)} />
              {invoice.sprayOperation.applicationMethod && (
                <InfoRow label="Method" value={invoice.sprayOperation.applicationMethod} />
              )}
              {invoice.sprayOperation.notes && (
                <InfoRow label="Notes" value={invoice.sprayOperation.notes} />
              )}
            </>
          )}

          {/* PDF Links */}
          <SectionHead title="Documents" />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <button
              onClick={generateInvoicePdf}
              disabled={invoicePdfBusy || needsPricingReview}
              style={{
                alignSelf: 'flex-start',
                background: 'none', border: 'none', padding: 0,
                fontSize: 13, color: 'var(--brand, #558b6e)', fontWeight: 550,
                textDecoration: 'underline', textUnderlineOffset: 2,
                cursor: invoicePdfBusy ? 'wait' : 'pointer',
                fontFamily: 'inherit'
              }}
            >
              {needsPricingReview ? 'Invoice PDF available after pricing review' : (invoicePdfBusy ? 'Generating Invoice PDF…' : 'Invoice PDF')}
            </button>
            {invoicePdfError && (
              <div style={{ fontSize: 11, color: '#c62828' }}>{invoicePdfError}</div>
            )}
            <button
              onClick={generateSubSprayLogPdf}
              disabled={subSprayPdfBusy}
              style={{
                alignSelf: 'flex-start',
                background: 'none', border: 'none', padding: 0,
                fontSize: 13, color: 'var(--brand, #558b6e)', fontWeight: 550,
                textDecoration: 'underline', textUnderlineOffset: 2,
                cursor: subSprayPdfBusy ? 'wait' : 'pointer',
                fontFamily: 'inherit'
              }}
            >
              {subSprayPdfBusy ? 'Generating Sub Spray Log…' : 'Sub Spray Log PDF'}
            </button>
            {subSprayPdfError && (
              <div style={{ fontSize: 11, color: '#c62828' }}>{subSprayPdfError}</div>
            )}
            <button
              onClick={generateHmiSprayLogPdf}
              disabled={hmiSprayPdfBusy}
              style={{
                alignSelf: 'flex-start',
                background: 'none', border: 'none', padding: 0,
                fontSize: 13, color: 'var(--brand, #558b6e)', fontWeight: 550,
                textDecoration: 'underline', textUnderlineOffset: 2,
                cursor: hmiSprayPdfBusy ? 'wait' : 'pointer',
                fontFamily: 'inherit'
              }}
            >
              {hmiSprayPdfBusy ? 'Generating Our Spray Log…' : 'Our Spray Log PDF'}
            </button>
            {hmiSprayPdfError && (
              <div style={{ fontSize: 11, color: '#c62828' }}>{hmiSprayPdfError}</div>
            )}
          </div>

          {/* Adjustments */}
          <SectionHead title="Adjustments" />
          {invoice.adjustments?.length === 0 && (
            <div style={{ fontSize: 12.5, color: 'var(--ink-3)', marginBottom: 12 }}>No adjustments.</div>
          )}
          {invoice.adjustments?.length > 0 && (
            <table className="hmi-table" style={{ marginBottom: 12 }}>
              <thead>
                <tr>
                  <th>Description</th>
                  <th style={{ textAlign: 'right' }}>Amount</th>
                  <th>By</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {invoice.adjustments.map((adj, i) => (
                  <tr key={i}>
                    <td>{adj.description}</td>
                    <td className="num">{fmtCurrency(adj.amount)}</td>
                    <td style={{ fontSize: 12, color: 'var(--ink-3)' }}>
                      {adj.adjustedBy ? `${adj.adjustedBy.firstName || ''} ${adj.adjustedBy.lastName || ''}`.trim() : '—'}
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--ink-3)' }}>{fmtDateShort(adj.adjustedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {/* Add Adjustment Form */}
          {canAdjust && (
            <form onSubmit={handleAdjust} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--ink-3)', marginBottom: 2 }}>Add Adjustment</div>
              <input
                type="text"
                placeholder="Description"
                value={adjDesc}
                onChange={e => setAdjDesc(e.target.value)}
                style={{
                  height: 30, padding: '0 10px', borderRadius: 5,
                  border: '1px solid var(--line-2, #cfd9dc)',
                  fontSize: 13, fontFamily: 'var(--f-sans, inherit)',
                  background: 'var(--bg-panel, #fff)', color: 'var(--ink)',
                }}
              />
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  type="number"
                  placeholder="Amount (+ or -)"
                  value={adjAmount}
                  onChange={e => setAdjAmount(e.target.value)}
                  step="0.01"
                  style={{
                    height: 30, padding: '0 10px', borderRadius: 5, flex: 1,
                    border: '1px solid var(--line-2, #cfd9dc)',
                    fontSize: 13, fontFamily: 'var(--f-sans, inherit)',
                    background: 'var(--bg-panel, #fff)', color: 'var(--ink)',
                  }}
                />
                <button
                  type="submit"
                  className="hmi-btn hmi-btn-outline hmi-btn-sm"
                  disabled={adjSubmitting}
                  style={{ minWidth: 80 }}
                >
                  {adjSubmitting ? 'Saving…' : 'Add'}
                </button>
              </div>
              {adjError && (
                <div style={{ fontSize: 12, color: '#c62828', padding: '4px 0' }}>{adjError}</div>
              )}
            </form>
          )}
        </div>
      )}
    </div>
  )
}
